import React from "react";
import { Facebook, Instagram } from "lucide-react";
import { Link } from "react-router-dom";

export default function Footer() {
    return (
        <footer className="relative bg-white pt-20 text-ink" data-testid="footer">
            <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
                <div className="grid gap-10 border-b border-ink/10 pb-12 lg:grid-cols-12">
                    <div className="lg:col-span-6">
                        <h3 className="font-display text-3xl font-black leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
                            Ready to grow{" "}
                            <span className="font-serif italic font-normal text-coral">
                                steady?
                            </span>
                        </h3>
                        <Link
                            to="/#contact"
                            className="mt-6 inline-flex items-center gap-2 border-b-2 border-coral pb-1 font-display text-base font-bold uppercase tracking-wider text-ink"
                            data-testid="footer-cta"
                        >
                            Book a free strategy call →
                        </Link>
                    </div>

                    <div className="grid grid-cols-2 gap-8 text-sm lg:col-span-6 lg:grid-cols-3">
                        <FCol title="Studio">
                            <FLink href="/#pricing">Packages</FLink>
                            <FLink to="/library">Library</FLink>
                            <FLink href="/#playbook">7-Day Checklist</FLink>
                        </FCol>
                        <FCol title="Company">
                            <FLink to="/blog">Blog</FLink>
                            <FLink href="/#about">About</FLink>
                            <FLink href="/#contact">Contact</FLink>
                            <Link
                                to="/admin/login"
                                className="block text-ink/65 hover:text-coral"
                                data-testid="footer-admin-link"
                            >
                                Admin
                            </Link>
                        </FCol>
                        <FCol title="Follow">
                            <div className="flex flex-wrap items-center gap-3 pt-1">
                                <SocialBtn label="Instagram" href="https://instagram.com/simpledigital.co.nz">
                                    <Instagram className="h-4 w-4" />
                                </SocialBtn>
                                <SocialBtn label="Facebook" href="https://facebook.com/simpledigital.co.nz">
                                    <Facebook className="h-4 w-4" />
                                </SocialBtn>
                                <SocialBtn label="TikTok" href="https://tiktok.com/@simpledigital.co.nz">
                                    <TikTokGlyph />
                                </SocialBtn>
                                <SocialBtn label="Pinterest" href="https://pinterest.com/simpledigitalconz">
                                    <PinterestGlyph />
                                </SocialBtn>
                            </div>
                        </FCol>
                    </div>
                </div>

                <div className="pointer-events-none select-none py-10 text-center">
                    <span className="block font-display text-[18vw] font-black leading-none tracking-tight text-ink lg:text-[14rem]">
                        simple<span className="text-coral">.</span>digital
                    </span>
                </div>

                <div className="flex flex-col items-start justify-between gap-3 border-t border-ink/10 py-6 text-xs uppercase tracking-widest text-ink/45 lg:flex-row lg:items-center">
                    <span>© {new Date().getFullYear()} Simple Digital. NZ + AU.</span>
                    <span>Made in Tāmaki Makaurau, with care.</span>
                </div>
            </div>
        </footer>
    );
}

function FCol({ title, children }) {
    return (
        <div>
            <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-coral">
                {title}
            </div>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

function FLink({ href, to, children }) {
    if (to) {
        return (
            <Link to={to} className="block text-ink/65 transition-colors hover:text-coral">
                {children}
            </Link>
        );
    }
    return (
        <a href={href} className="block text-ink/65 transition-colors hover:text-coral">
            {children}
        </a>
    );
}

function SocialBtn({ children, label, href }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            data-testid={`social-${label.toLowerCase()}`}
            className="grid h-9 w-9 place-items-center border-2 border-ink/20 text-ink/70 transition-all hover:-translate-y-0.5 hover:border-coral hover:bg-coral hover:text-white"
        >
            {children}
        </a>
    );
}

function TikTokGlyph() {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden>
            <path d="M19.32 6.14a5.28 5.28 0 0 1-3.72-1.42V15.5a5.83 5.83 0 1 1-5.83-5.83c.2 0 .4.01.6.03v2.66a3.19 3.19 0 1 0 2.55 3.14V2h2.66a5.29 5.29 0 0 0 3.74 4.14z" />
        </svg>
    );
}

function PinterestGlyph() {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden>
            <path d="M12 2C6.5 2 2 6.5 2 12c0 4.2 2.6 7.8 6.3 9.3-.1-.8-.2-2 0-2.9.2-.8 1.2-5.1 1.2-5.1s-.3-.6-.3-1.5c0-1.4.8-2.5 1.8-2.5.9 0 1.3.7 1.3 1.5 0 .9-.6 2.2-.9 3.5-.2 1 .5 1.9 1.5 1.9 1.9 0 3.3-2 3.3-4.8 0-2.5-1.8-4.3-4.4-4.3-3 0-4.7 2.2-4.7 4.5 0 .9.3 1.9.8 2.4.1.1.1.2.1.3 0 .1-.1.4-.1.5-.1.2-.2.3-.4.2-1.4-.7-2.3-2.7-2.3-4.4 0-3.6 2.6-6.9 7.5-6.9 3.9 0 7 2.8 7 6.5 0 3.9-2.5 7.1-5.9 7.1-1.2 0-2.3-.6-2.6-1.3l-.7 2.7c-.3 1-1 2.3-1.4 3.1.9.3 2 .4 3 .4 5.5 0 10-4.5 10-10S17.5 2 12 2z" />
        </svg>
    );
}
